"use client";

import "@/lib/utils/dom-translation-patch";

import { CookieConsentBanner } from "@/components/cookie-consent/cookie-consent-banner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { broadcastAuthEvent } from "@/lib/auth/auth-channel";
import { buildSessionExpiredSignInHref } from "@/lib/auth/redirects";
import { ErrorBoundary } from "@/lib/error/boundaries/ErrorBoundary";
import { BreadcrumbProvider } from "@/lib/providers/breadcrumb-provider";
import { GeolocationProvider } from "@/lib/providers/geolocation-provider";
import { isBffError } from "@/lib/utils/bff-fetch";
import { logger } from "@/lib/utils/logger";
import {
  retryDelay,
  shouldRetryMutation,
  shouldRetryQuery,
} from "@/lib/utils/query-retry";
import { PostHogProvider } from "@posthog/react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import posthog from "posthog-js";
import { ReactNode, useState } from "react";
import { Toaster } from "sonner";

/**
 * Paths where a 401 should NOT trigger a forced sign-out redirect.
 * Users on these routes are either unauthenticated by design (sign-in,
 * cookie policy) or mid-flow and a redirect would clobber state.
 */
const SIGNOUT_EXEMPT_PREFIXES = [
  "/signin",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/cookie-policy",
];

/**
 * Single-flight guard for the global 401 handler. When a query or
 * mutation hits 401, many siblings often fail in the same tick (e.g. on
 * window-focus refetch). We only want one logout broadcast + one
 * redirect per fan-out.
 */
let handlingSignout = false;

function shouldHandleAuthError(status: number): boolean {
  if (status !== 401) return false;
  if (typeof window === "undefined") return false;
  if (handlingSignout) return false;
  const { pathname } = window.location;
  return !SIGNOUT_EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Handle a hard 401 from the BFF layer.
 *
 * By the time we get here, `resolveActiveClientId` + `backendFetch` on
 * the server have already tried to refresh; a 401 reaching the browser
 * means the refresh token is also gone/revoked. Clean up and bounce to
 * sign-in so the user isn't stuck watching retry storms.
 */
function handleGlobalAuthFailure(endpoint: string | undefined): void {
  if (handlingSignout) return;
  handlingSignout = true;

  logger.warn("Global 401 handler: forcing sign-out", { endpoint });

  try {
    broadcastAuthEvent({ type: "logout" });
  } catch (error) {
    logger.warn("Failed to broadcast logout on 401", {
      error: error instanceof Error ? error.message : String(error),
    });
  }

  const current = window.location.pathname + window.location.search;
  const href = buildSessionExpiredSignInHref(current);
  if (href) {
    window.location.href = href;
  } else {
    // Already on an auth page — allow a future 401 to re-trigger.
    handlingSignout = false;
  }
}

interface ProvidersProps {
  readonly children: ReactNode;
}

/**
 * Main Providers Wrapper
 */
export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        // Fan-in 401/403 handling: the BFF has already attempted a
        // silent refresh (via resolveActiveClientId + backendFetch). A
        // 401 reaching the browser is definitive — force a sign-out
        // rather than letting failing queries loop.
        queryCache: new QueryCache({
          onError: (error) => {
            if (!isBffError(error)) return;
            if (error.status === 403) {
              // 403 is not an auth-session failure — user is signed in
              // but lacks permission. Surface it via the component's
              // own error UI; do not redirect.
              return;
            }
            if (shouldHandleAuthError(error.status)) {
              handleGlobalAuthFailure(error.endpoint);
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error) => {
            if (!isBffError(error)) return;
            if (shouldHandleAuthError(error.status)) {
              handleGlobalAuthFailure(error.endpoint);
            }
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 2 * 60 * 1000,
            gcTime: 10 * 60 * 1000, // 10 min cache
            retry: shouldRetryQuery,
            retryDelay,
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
          },
          mutations: {
            retry: shouldRetryMutation,
            retryDelay,
          },
        },
      }),
  );

  return (
    <PostHogProvider client={posthog}>
      <Toaster
        position="bottom-right"
        expand={false}
        richColors
        closeButton
        toastOptions={{
          classNames: {
            toast:
              "bg-white border-separator-primary text-text-primary shadow-lg",
            title: "text-text-primary font-semibold",
            description: "text-text-secondary",
            closeButton:
              "bg-gray-50 text-text-muted hover:bg-gray-100 hover:text-text-primary",
            actionButton:
              "bg-graphene hover:bg-brand-700 text-white font-medium px-3 py-1.5 rounded-md transition-colors",
            info: "bg-white border-l-4 border-l-graphene border-separator-primary",
          },
        }}
      />
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <GeolocationProvider>
            <BreadcrumbProvider>
              <TooltipProvider delay={200}>{children}</TooltipProvider>
            </BreadcrumbProvider>
          </GeolocationProvider>
          <ReactQueryDevtools initialIsOpen={false} position="bottom" />
        </QueryClientProvider>
      </ErrorBoundary>
      <CookieConsentBanner />
    </PostHogProvider>
  );
}
